<template>
  <div class="mt-4">
    <div class="container">
      <div class="row" v-if="isLoading">
        <div class="col-12 text-center">
          <HMSpinner />
        </div>
      </div>
      <div class="row" v-if="!isLoading && hasGroups">
        <div v-if="!hasGroups" class="text-center w-100">
          <HMEmpty
            image=""
            :title="$t('COMMUNITY.TITLE.NO_GROUPS')"
            :description="$t('COMMUNITY.TEXT.NO_COMMUNITY_GROUP_ACCESS')"
          />
        </div>
        <div class="col-12 col-md-7">
          <div class="border p-4 rounded">
            <div class="row">
              <div class="col-12">
                <h3 class="mb-4">{{ $t('STUDIO.TITLE.COMMUNITY') }}</h3>
              </div>
              <div
                class="col-12 col-lg-6 mb-4"
                v-for="group in groups"
                :key="group.id"
              >
                <NuxtLink
                  class="undecorated-router-link"
                  :is="group.user_left_this_group ? 'span' : 'router-link'"
                  :to="{
                    name: 'studio-community-group',
                    params: { group_id: group.id, studio_url: studioURL },
                  }"
                  @click="$emit('close')"
                >
                  <StudioCommunityGroupCard :group="group" />
                </NuxtLink>
              </div>
            </div>
          </div>
        </div>
        <div class="col-12 col-md-5 mt-4 mt-md-0">
          <div class="border p-4 rounded h-100">
            <div class="row">
              <div class="col-12">
                <CommunityNotificationsList
                  class="community-notifications-list"
                  :maxNotifications="5"
                  :magicCode="studentMagicCode"
                >
                  <template v-slot:title>
                    <h3 class="mb-4">
                      {{ $t('STUDIO.TITLE.RECENT_NOTIFICATIONS') }}
                    </h3>
                  </template>
                  <template v-slot:no-data>
                    <HMNoDataCard
                      class="p-0"
                      :showBorder="false"
                      :title="$t('STUDIO.TITLE.NOTIFICATIONS')"
                      :description="
                        $t(
                          'STUDIO.TEXT.YOU_HAVE_NO_RECENT_NOTIFICATIONS_IN_YOUR_COMMUNITIES'
                        )
                      "
                    />
                  </template>
                </CommunityNotificationsList>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="row" v-if="!isLoading && !hasGroups">
        <div class="col-12">
          <HMNoDataCard
            :title="$t('COMMUNITY.TITLE.NO_GROUPS')"
            :description="$t('COMMUNITY.TEXT.NO_COMMUNITY_GROUP_ACCESS')"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
definePageMeta({
  name: 'studio-community',

  isStudio: true,
  hideInstagramSlider: true,
  showStudentNavigation: true,}
)
</script>

<script lang="ts">
import { mapState } from 'pinia';
import { errorToMessage } from '@/utils/errors';
import RefreshData from '@/mixins/refreshData';
import StudioCommunityGroupCard from '@/components/community/StudioCommunityGroupCard.vue';
import CommunityNotifications from '@/components/community/CommunityNotifications.vue';
import CommunityNotificationsList from '@/components/community/CommunityNotificationsList.vue';
import { useCommunityStore, useUserStore } from '@/stores';

export default {
  name: 'StudioCommunity',
  mixins: [RefreshData],
  components: {
    StudioCommunityGroupCard,
    CommunityNotifications,
    CommunityNotificationsList,
  },
  computed: {
    ...mapState(useUserStore, {
      studentMagicCode: 'studentMagicCode',
    }),
    ...mapState(useCommunityStore, {
      interactAsStudio: 'interactAsStudio',
    }),
    studioURL() {
      return this.$studioURL;
    },
    hasGroups() {
      return this.groups?.length > 0;
    },
  },
  data() {
    return {
      isLoading: true,
      groups: [],
    };
  },
  methods: {
    async loadData() {
      this.isLoading = true;
      try {
        this.groups = await useCommunityStore().loadStudioGroups({
          studioURL: this.studioURL,
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('NOTIFICATIONS.TITLE.ERROR') as string,
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1,
        });
      } finally {
        this.isLoading = false;
      }
    },
  },
  mounted() {
    this.loadData();
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/components-variables.scss';
</style>
